import {
    TelemetryChannel,
    TelemetryType,
} from '@tableau/microview-communication-service/lib/src/channelDefinitions/TelemetryChannelDefinitions';

export class TelemetryService {
    public static instance = new TelemetryService();
    private channel = new TelemetryChannel();

    public sendSearchInitiatedEvent(event: Omit<SearchInitiatedEvent, 'searchFlowId'>) {
        return this.channel.call({ bag: event, type: TelemetryType.SearchInitiated });
    }

    public sendSearchResultClickedEvent(event: Omit<SearchResultClickedEvent, 'searchFlowId'>, skipClearSearchFlowId?: boolean) {
        return this.channel.call({ bag: event, type: TelemetryType.ClickSearchResultDirectlyToTelemetryService, skipClearSearchFlowId: skipClearSearchFlowId });
    }

    public sendSearchFiltersUpdatedEvent(event: Omit<SearchFiltersUpdatedEvent, 'searchFlowId'>, skipClearSearchFlowId?: boolean) {
        return this.channel.call({ bag: event, type: TelemetryType.SearchFiltersUpdated });
    }

    public sendSearchTermUpdatedEvent(event: Omit<SearchTermUpdatedEvent, 'searchFlowId'>) {
        return this.channel.call({ bag: event, type: TelemetryType.SearchTermUpdate });
    }

    public sendSearchEPTEvent(event: Omit<SearchEPTEvent, 'searchFlowId'>) {
        return this.channel.call({ bag: event, type: TelemetryType.SearchEPT });
    }

    public sendSearchNoResultEvent(event: Omit<SearchNoResultEvent, 'searchFlowId'>) {
        return this.channel.call({ bag: event, type: TelemetryType.SearchNoResult });
    }

    public sendSearchImpressionEvent(event: Omit<SearchImpressionEvent, 'searchFlowId'>) {
        return this.channel.call({ bag: event, type: TelemetryType.SearchImpression });
    }

}

export type SearchEventSource = 'omni' | 'search-space';

export enum TelemetryEvents {
    SearchResultClicked = 'SearchResultClicked',
    SearchEPT = 'search_ept',
    SearchFiltersUpdated = 'search_filters_update',
    SearchImpression = 'SearchImpression',
    SearchInitiated = 'SearchInitiated',
    SearchNoResult = 'SearchNoResult',
    SearchTermUpdated = 'SearchTermUpdated'
}

interface SearchTelemetryEventBase {
    host_component: string;
}

export interface SearchResultClickedEvent extends SearchTelemetryEventBase {
    type: string;
    eventSource: string;
    resultType: string;
    seeAll: boolean;
    index: number;
    total: number;
    searchTerm: string;
    contentId: string;
    searchFlowId: string;
}

export interface SearchEPTEvent extends SearchTelemetryEventBase {
    type: TelemetryEvents.SearchEPT;
    ept_type: string;
    search_term: string;
    search_flow_id: string;
    duration_from_start_to_phase_a: number; // Phase A means initial rendering (like a loading indicator.)
    duration_from_phase_a_to_phase_b: number; // Phase B means the time when calling server side APIs
    duration_from_phase_b_to_phase_c: number; // Phase C means the time when all the server side responses are returned
    duration_from_phase_c_to_end: number; // End means the time when the final UI is rendered.
    total_duration: number;
}

export interface SearchFiltersUpdatedEvent extends SearchTelemetryEventBase {
    type: TelemetryEvents.SearchFiltersUpdated;
    search_term: string;
    filters_added?: string;
    filters_removed?: string;
    filters_updated?: string;
}

export interface SearchImpressionEvent extends SearchTelemetryEventBase {
    type: TelemetryEvents.SearchImpression;
    searchTerm: string;
    search_flow_id: string;
    impression_type: string;
}

export interface SearchInitiatedEvent extends SearchTelemetryEventBase {
    type: TelemetryEvents.SearchInitiated;
    eventSource: SearchEventSource;
    searchFlowId: string;
}

export interface SearchNoResultEvent extends SearchTelemetryEventBase {
    type: TelemetryEvents.SearchNoResult;
    eventSource: SearchEventSource;
    searchTerm: string;
    searchFlowId: string;
}

export interface SearchTermUpdatedEvent extends SearchTelemetryEventBase {
    type: TelemetryEvents.SearchTermUpdated;
    eventSource: SearchEventSource;
    searchTerm: string;
    searchFlowId: string;
}
